// Der Abschnitt "Verlauf" des Kontaktdetails -- aus kontakt.tsx herausgeloest
// (Party 360, erster Schnitt; Verhalten unveraendert).

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactElement,
} from 'react';
import {
  Body1,
  Button,
  Caption1,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Option,
  Subtitle1,
  Textarea,
  mergeClasses,
} from '@fluentui/react-components';
import {
  cancelFollowUp,
  changeFollowUpDue,
  completeFollowUp,
  createFollowUp,
  dueLabel,
  dueOnFieldProblem,
  FAELLIGKEITEN,
  faelligkeitFuer,
  followUpButtonName,
  reopenFollowUp,
  titleFieldProblem,
  anlassAusNotiz,
  todayInBerlin,
  type FaelligkeitId,
} from '../../tasks.ts';
import {
  createNote,
  emptyFilterSentence,
  fetchInteractions,
  filteredHistorySummary,
  filterOptionen,
  filtern,
  forgetNoteDraft,
  historySummary,
  noteSavedAnnouncement,
  newClientRequestId,
  noteFieldProblem,
  recallNoteDraft,
  rememberNoteDraft,
  validationMessageFor,
  type InteractionView,
  type VerlaufFilterId,
} from '../../interactions.ts';
import type { PlainMessage } from '../../access.ts';
import { useScreenStyles } from '../../shell/screen-styles.ts';
import { Schaltergruppe } from '../../shell/schaltergruppe.tsx';
import { Skelett, SKELETT_VERLAUF } from '../../shell/skelett.tsx';
import { Vorgang, Zustand } from '../../shell/zustand.tsx';
import { istMacTastatur } from '../../shell/tastenkuerzel.ts';
import { Tasten } from '../../shell/taste.tsx';
import { useKontaktStyles } from './stile.ts';
import { tabId, NOTE_INPUT_ID, HISTORY_STATUS_ID, FOLLOW_UP_CHOICE_ID, FOLLOW_UP_DATE_ID, FOLLOW_UP_TITLE_ID } from './ids.ts';
import type { KontaktScreenProps } from './typen.ts';
import { tallyFollowUps, type FollowUpTally } from '../../party-360.ts';
import { eintragAusloeserId, eintragId, notizBezugVon, VerlaufEintrag } from './verlauf-eintrag.tsx';
import { beiEscapeSchliessen } from './plus-symbol.tsx';
import { NebenKnopf } from '../../shell/nebenknopf.tsx';
import { meldungsIntent } from '../../shell/use-section-commands.ts';

/** Die Kennungen der Eingabe "Wiedervorlage" -- nur dieser Abschnitt braucht sie. */
const ERLEDIGT_SATZ = 'Erledigt.';
/** So viele Eintraege zeigt der Ueberblick; der ganze Verlauf steht im Register (M-12). */
const KOMPAKT_EINTRAEGE = 5;

/** Uhr mit Pfeil zurueck (Verlauf), nur Zierde: der Text traegt die Aussage. */
function VerlaufSymbol(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width={16} height={16} aria-hidden focusable={false} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 12a8 8 0 1 0 2.3-5.6M4 4v4h4M12 8v4l3 2" />
    </svg>
  );
}
const FOLLOW_UP_OTHER_DAY_ID = 'kontakt-wiedervorlage-anderer-tag';
const FOLLOW_UP_REASON_ID = 'kontakt-wiedervorlage-anlass';

/** Wiedervorlage ohne Antwort: der Ausgang ist offen; der Verlauf hat keinen eigenen Neuladen-Knopf. */
function wiedervorlageOffen(verb: string): PlainMessage {
  return {
    headline: `Ob die Wiedervorlage ${verb} wurde, ist offen.`,
    explanation: 'Die Antwort kam nicht an.',
    nextStep: 'Lade die Seite neu. Dann siehst du, was gilt.',
    retryable: false,
  };
}

/** Die Anmeldung scheiterte vor dem Senden: sicher nichts geaendert. */
const WIEDERVORLAGE_NICHT_GESENDET: PlainMessage = Object.freeze({
  headline: 'Deine Wiedervorlage wurde nicht geändert.',
  explanation: 'Deine Anmeldung ließ sich gerade nicht erneuern. Gesendet wurde nichts.',
  nextStep: 'Versuche es erneut.',
  retryable: true,
});

/**
 * Der Verlauf (ST-ACT-001, erster Schnitt; ADR-0051).
 *
 * WAS ER ZEIGT: die Notizen dieses Kontakts, neueste zuerst, je mit Art,
 * Herkunft, Autor in Worten ("von dir") und Zeitpunkt (5.2 Nr. 5 Source-aware,
 * soweit ohne Verzeichnis moeglich). Sichtbarkeit und Kontext entscheidet der
 * Server; der Bildschirm zeigt, was er bekommt, und erfindet keine Erklaerung
 * fuer das, was fehlt.
 *
 * WAS ER NICHT ZEIGT (ADR-0002: keine Attrappe): Filter nach Kanal -- es gibt
 * einen; eine Wiedervorlage -- sie ist der zweite Schnitt; eine Auswahl der
 * Sichtbarkeit -- sie ist fest "Team" und wird gesagt, nicht gefragt.
 *
 * EINE HAUPTAKTION: "Notiz festhalten". Zeitpunkt (jetzt), Autor, Bezug und
 * Sichtbarkeit setzt der Server (CM-ACT-005). Der Text bleibt bei jeder
 * Ablehnung stehen (NFR-UX-005) und ueberlebt als Entwurf Neuladen und
 * Absturz, gebunden an Konto und Kontakt (CM-UX-004; interactions.ts).
 *
 * GENAU EINE STATUSREGION dieses Abschnitts mit aria-live; der Fokus wandert
 * nach dem Speichern dorthin (Muster A15), bei einem Feldfehler ins Feld.
 */
export function Verlauf({
  apiBaseUrl,
  accessToken,
  onReauthenticate,
  partyId,
  accountId,
  onStand,
  nurLesen = false,
  onNichtBeschreibbar,
  kompakt = false,
  onGanzerVerlauf,
}: KontaktScreenProps & {
  /** Im Ueberblick: nur die neuesten Eintraege, ohne Filter und Nachladen (M-12). */
  readonly kompakt?: boolean;
  /** Wechselt ins Register "Verlauf"; fehlt er, gibt es den Knopf nicht. */
  readonly onGanzerVerlauf?: () => void;
  /**
   * Party 360, erster Schnitt: der Verlauf meldet nach jeder Aenderung seiner
   * geladenen Notizen die Summe der offenen Wiedervorlagen an den Bildschirm,
   * der sie im Kopf nennt -- ohne eigenen Abruf (party-360.ts).
   */
  readonly onStand?: (tally: FollowUpTally | undefined) => void;
  /** Der Kontakt ist nicht beschreibbar: keine Eingabe, kein Vormerken (Server: 409 party_not_writable). */
  readonly nurLesen?: boolean;
  /** Der Server lehnte mit 409 party_not_writable ab: die Seite liest den Kontakt neu, damit die Leiste den Zustand zeigt. */
  readonly onNichtBeschreibbar?: () => void;
}): ReactElement {
  const styles = useScreenStyles();
  const own = useKontaktStyles();
  const [eintraege, setEintraege] = useState<readonly InteractionView[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<'laedt' | 'geladen' | 'abgelehnt'>('laedt');
  // Jede Aenderung der geladenen Notizen -- Laden, Weiterblaettern, eine neue
  // oder abgehakte Wiedervorlage -- erreicht den Kopf; er zeigt nie einen
  // aelteren Stand als der Verlauf selbst. Waehrend des Neuladens und nach
  // einem gescheiterten Neuladen gibt es KEINEN Stand: der Kopf schweigt,
  // statt eine Zahl zu zeigen, die ein gelungener Befehl gerade ueberholt hat
  // (Codex-Cloud-Review #271, P2; Probe A49).
  useEffect(() => {
    onStand?.(status === 'geladen' ? tallyFollowUps(eintraege, hasMore) : undefined);
  }, [eintraege, hasMore, status, onStand]);
  const [message, setMessage] = useState<PlainMessage | undefined>(undefined);
  // Ob die Meldung einen offenen Ausgang nennt; gesetzt dort, wo der Satz gewaehlt wird.
  const [offen, setOffen] = useState(false);
  /**
   * Ein gescheiterter Abruf des Verlaufs -- GETRENNT von der Meldung eines
   * Befehls: sonst bliebe nach einem gelungenen "Verlauf erneut laden" die
   * alte Stoerung stehen, oder ein Neuladen nach einem Konflikt nahme dessen
   * Satz mit (Codex-Cloud-Review PR #247). Traegt den Cursor der gescheiterten
   * Seite, damit die Wiederholung DIESE Seite laedt und nicht die erste.
   */
  const [ladefehler, setLadefehler] = useState<{ readonly message: PlainMessage; readonly cursor?: string } | undefined>(undefined);
  const [bestaetigung, setBestaetigung] = useState<string | undefined>(undefined);
  // Der Rueckweg nach "Erledigt": taskId und neue lockVersion aus dessen Antwort. Er gilt nur,
  // solange die Statusregion noch SEINEN Satz zeigt; jede neue Ansage nimmt den Knopf mit.
  const [rueckgang, setRueckgang] = useState<
    { readonly satz: string; readonly taskId: string; readonly lockVersion: number; readonly interactionId: string } | undefined
  >(undefined);
  const [entwurf] = useState(() => recallNoteDraft(accountId, partyId));
  const [text, setText] = useState<string>(entwurf?.text ?? '');
  const [feldfehler, setFeldfehler] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const statusRef = useRef<HTMLDivElement | null>(null);
  const [fokusAnfordern, setFokusAnfordern] = useState(0);
  const laufRef = useRef(0);
  /**
   * Der zuletzt geladene Stand. `load` braucht ihn beim Nachladen und gibt
   * die neue Zahl zurueck -- `eintraege` traegt sie im selben Durchlauf noch
   * nicht, und die Bestaetigung soll sie nennen.
   */
  const eintraegeRef = useRef<readonly InteractionView[]>([]);
  /**
   * Nach einem Befehl laedt der Verlauf still nach: Liste, Filter und Zahl am
   * Register bleiben stehen (M2, M4), statt kurz zu verschwinden und die Seite
   * springen zu lassen. Der Spinner gehoert dem ERSTEN Laden und dem Wechsel
   * des Kontakts.
   */
  const statusRef2 = useRef<'laedt' | 'geladen' | 'abgelehnt'>('laedt');
  const geladenFuer = useRef<string | undefined>(undefined);
  /**
   * Die Anfragekennung des LAUFENDEN Absendevorgangs. Sie entsteht beim
   * ersten Klick und bleibt, bis der Server geantwortet hat -- ein zweiter
   * Klick oder eine Wiederholung nach Netzabbruch traegt dieselbe Kennung und
   * wird serverseitig zum 409 statt zur zweiten Notiz (Story-Akzeptanz 2).
   */
  const requestIdRef = useRef<string | undefined>(entwurf?.requestId);
  /**
   * Der Text, mit dem die Kennung zuletzt gesendet wurde. Aendert ihn die
   * Nutzerin nach einem offenen Ausgang, ist es eine neue Notiz mit neuer
   * Kennung: derselbe Schluessel mit anderem Text liefe sonst in den 409 der
   * ersten Notiz, und die Aenderung ginge verloren.
   */
  const gesendeterTextRef = useRef<string | undefined>(undefined);
  /**
   * Die Wiedervorlage an einer Notiz (ST-ACT-001, zweiter Schnitt; ADR-0052).
   *
   * GENAU EIN FORMULAR IST OFFEN -- `vormerkenFuer` traegt die Notiz, an der
   * gerade etwas gesetzt oder verschoben wird. Zwei offene Formulare waeren
   * zwei Hauptaktionen in einem Abschnitt (5.2 Nr. 2), und die Frage "welcher
   * Termin gehoert wohin" haette keine sichtbare Antwort.
   *
   * KEINE ZWEITE STATUSREGION IM ABSCHNITT: Bestaetigung und Ablehnung gehen
   * in dieselbe Region wie die Notiz (NFR-ACC-001; mit der TAX-Session am
   * 17.09.2026 abgestimmt). Der Fokus wandert danach dorthin -- Muster A15.
   * Eine EINZIGE Region im Rahmen des Bildschirms waere technisch moeglich,
   * aber unzumutbar umstaendlich: der Fokus landete nach jeder Aktion oberhalb
   * der Kontaktwege, und die Nutzerin muesste sich zum Ergebnis zurueck
   * tabben (dieselbe Lage, die Muster A14 als Fehler benennt).
   */
  const [vormerkenFuer, setVormerkenFuer] = useState<{ readonly interactionId: string; readonly taskId?: string; readonly lockVersion?: number } | undefined>(undefined);
  const [termin, setTermin] = useState('');
  const [titel, setTitel] = useState('');
  const [terminFehler, setTerminFehler] = useState<string | undefined>(undefined);
  const [titelFehler, setTitelFehler] = useState<string | undefined>(undefined);
  const heute = todayInBerlin();
  // Die Eingabe "Wiedervorlage" (Slice 5a): eine Notiz mit Wiedervorlage in einem Zug.
  // Aus dem Entwurf; eine unbekannte Option (aelterer oder anderer Stand) gilt als "keine".
  const [faelligkeit, setFaelligkeit] = useState<FaelligkeitId>(
    () => FAELLIGKEITEN.find((option) => option.id === entwurf?.faelligkeit)?.id ?? 'keine',
  );
  // Der Tag gilt nur zu "anderer Tag": zu einer unbekannten oder abgewaehlten Option bleibt er leer,
  // damit er bei einer spaeteren Wahl nicht ungefragt auftaucht.
  const [andererTag, setAndererTag] = useState(() => (faelligkeit === 'anderer' ? (entwurf?.andererTag ?? '') : ''));
  const [andererFehler, setAndererFehler] = useState<string | undefined>(undefined);
  /**
   * Der Anlass der Wiedervorlage im Notizkasten (D-15): folgt dem Notizanfang,
   * bis jemand das Feld selbst aendert (`undefined` = folgt).
   */
  const [anlass, setAnlass] = useState<string | undefined>(() => (faelligkeit === 'keine' ? undefined : entwurf?.anlass));
  const [anlassFehler, setAnlassFehler] = useState<string | undefined>(undefined);
  const anlassWert = anlass ?? anlassAusNotiz(text);
  const [filter, setFilter] = useState<VerlaufFilterId>('alles');
  // "Alles zeigen" haengt sich selbst aus; der Fokus geht auf den Filterknopf.
  const [filterFokus, setFilterFokus] = useState(false);
  const filterLeisteRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (filterFokus && filter === 'alles') {
      setFilterFokus(false);
      filterLeisteRef.current?.querySelector<HTMLButtonElement>('button[aria-pressed="true"]')?.focus();
    }
  }, [filterFokus, filter]);
  // In dieser Sitzung erledigt: der Eintrag zeigt es, ohne dass die Seite springt.
  const [erledigt, setErledigt] = useState<ReadonlySet<string>>(() => new Set());
  // Notiz gespeichert, Wiedervorlage nicht: EINE Ansage, keine zwei.
  const [teilerfolg, setTeilerfolg] = useState<string | undefined>(undefined);
  const [fokusVormerken, setFokusVormerken] = useState(0);
  // Nach "Ganzen Verlauf anzeigen" der erste Eintrag, der vorher nicht zu sehen
  // war; gibt es ihn nicht (Filter im Register), das Register selbst. Nie body.
  const [fokusGanzer, setFokusGanzer] = useState(0);
  const mac = istMacTastatur();

  useEffect(() => {
    if (fokusAnfordern > 0) {
      statusRef.current?.focus();
    }
  }, [fokusAnfordern]);

  // Nach einem Teilerfolg steht das Vormerkformular offen: der Fokus geht
  // dorthin, in den Tag -- NACH dem Rerender, sonst traefe er ein Feld, das es
  // noch nicht gibt.
  useEffect(() => {
    if (fokusVormerken > 0) {
      document.getElementById(FOLLOW_UP_DATE_ID)?.focus();
    }
  }, [fokusVormerken]);

  useEffect(() => {
    if (fokusGanzer === 0 || kompakt) return;
    setFokusGanzer(0);
    const ziel = filtern(eintraegeRef.current, filter)[KOMPAKT_EINTRAEGE];
    (ziel === undefined ? document.getElementById(tabId('verlauf')) : document.getElementById(eintragId(ziel.interactionId)))?.focus();
  }, [fokusGanzer, kompakt, filter]);

  // Nach "Abbrechen" ist das Formular weg: der Fokus geht an den Ausloeser
  // zurueck ("Wiedervorlage setzen" bzw. das Menue "…"), nicht auf den Body
  // (WCAG 2.4.3). Erst nach dem Rerender, dann gibt es ihn wieder.
  const [fokusAusloeser, setFokusAusloeser] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (fokusAusloeser !== undefined) {
      setFokusAusloeser(undefined);
      document.getElementById(eintragAusloeserId(fokusAusloeser))?.focus();
    }
  }, [fokusAusloeser]);

  useEffect(() => {
    eintraegeRef.current = eintraege;
  }, [eintraege]);

  useEffect(() => {
    statusRef2.current = status;
  }, [status]);

  useEffect(() => {
    rememberNoteDraft(accountId, partyId, text, undefined, Date.now(), requestIdRef.current, { faelligkeit, andererTag, anlass });
  }, [accountId, partyId, text, faelligkeit, andererTag, anlass]);

  const load = useCallback(
    async (fortsetzen?: string) => {
      const meinLauf = laufRef.current + 1;
      laufRef.current = meinLauf;
      const gueltig = (): boolean => laufRef.current === meinLauf;
      if (fortsetzen === undefined && !(statusRef2.current === 'geladen' && geladenFuer.current === partyId)) {
        setStatus('laedt');
      }
      // Ein Abruf raeumt die vorige Stoerung des Verlaufs ab; gelingt er
      // nicht, setzt der Fehlerzweig sie neu -- mit dem Cursor dieser Seite.
      setLadefehler(undefined);
      try {
        const token = await accessToken();
        if (!gueltig()) return;
        if (token === undefined) {
          onReauthenticate();
          return;
        }
        const result = await fetchInteractions({
          baseUrl: apiBaseUrl,
          accessToken: token,
          partyId,
          ...(fortsetzen === undefined ? {} : { cursor: fortsetzen }),
        });
        if (!gueltig()) return;
        if (result.outcome === 'not-found') {
          // Das Detail darueber hat denselben Ausgang bereits gemeldet.
          setEintraege([]);
          setStatus('geladen');
          return;
        }
        if (result.outcome === 'rejected') {
          setLadefehler({ message: result.message, ...(fortsetzen === undefined ? {} : { cursor: fortsetzen }) });
          setStatus(fortsetzen === undefined ? 'abgelehnt' : 'geladen');
          return;
        }
        const neueEintraege = fortsetzen === undefined ? result.interactions : [...eintraegeRef.current, ...result.interactions];
        eintraegeRef.current = neueEintraege;
        setEintraege(neueEintraege);
        setHasMore(result.hasMore);
        setCursor(result.nextCursor);
        setStatus('geladen');
        geladenFuer.current = partyId;
        // Der neue Stand geht an den Aufrufer zurueck: die Bestaetigung nennt
        // die Zahl, und `eintraege` traegt sie in diesem Durchlauf noch nicht.
        return { count: neueEintraege.length, hasMore: result.hasMore };
      } catch {
        if (!gueltig()) return;
        setLadefehler({
          message: {
            headline: 'Der Verlauf ist gerade nicht erreichbar.',
            explanation: 'Es kam keine Antwort.',
            nextStep: 'Prüfe deine Verbindung und versuche es erneut.',
            retryable: true,
          },
          ...(fortsetzen === undefined ? {} : { cursor: fortsetzen }),
        });
        setStatus(fortsetzen === undefined ? 'abgelehnt' : 'geladen');
      }
    },
    [accessToken, apiBaseUrl, onReauthenticate, partyId],
  );

  useEffect(() => {
    void load();
    return () => {
      laufRef.current += 1;
    };
  }, [load]);

  const festhalten = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const problem = noteFieldProblem(text);
    if (problem !== undefined) {
      setFeldfehler(problem);
      document.getElementById(NOTE_INPUT_ID)?.focus();
      return;
    }
    const gewuenschterTag = faelligkeit === 'anderer' ? andererTag : faelligkeitFuer(faelligkeit, heute);
    if (faelligkeit === 'anderer') {
      const tagProblem = dueOnFieldProblem(andererTag, heute);
      setAndererFehler(tagProblem);
      if (tagProblem !== undefined) {
        document.getElementById(FOLLOW_UP_OTHER_DAY_ID)?.focus();
        return;
      }
    }
    const gesendeterAnlass = anlassWert;
    if (gewuenschterTag !== undefined) {
      const anlassProblem = titleFieldProblem(gesendeterAnlass);
      setAnlassFehler(anlassProblem);
      if (anlassProblem !== undefined) {
        document.getElementById(FOLLOW_UP_REASON_ID)?.focus();
        return;
      }
    }
    setFeldfehler(undefined);
    setOffen(false);
    setMessage(undefined);
    setBestaetigung(undefined);
    setTeilerfolg(undefined);
    setBusy(true);
    // Wohin der Fokus nach der Antwort geht: in die Statusregion, ausser der
    // Server weist das FELD ab -- dann zurueck ins Feld, wie bei der Pruefung
    // vor dem Senden (Codex-Cloud-Review PR #247; NFR-UX-005, WCAG 2.4.3).
    let fokusZiel: 'status' | 'feld' | 'vormerken' = 'status';
    if (gesendeterTextRef.current !== undefined && gesendeterTextRef.current !== text.trim()) requestIdRef.current = undefined;
    requestIdRef.current ??= newClientRequestId();
    gesendeterTextRef.current = text.trim();
    // Die Kennung MIT dem Entwurf sichern, bevor gesendet wird: ein Abbruch
    // zwischen Commit und Antwort wiederholt dieselbe Sendung.
    rememberNoteDraft(accountId, partyId, text, undefined, Date.now(), requestIdRef.current, { faelligkeit, andererTag, anlass });
    let gesendet = false;
    try {
      const token = await accessToken();
      if (token === undefined) {
        onReauthenticate();
        return;
      }
      gesendet = true;
      const result = await createNote({
        baseUrl: apiBaseUrl,
        accessToken: token,
        partyId,
        summary: text.trim(),
        clientRequestId: requestIdRef.current,
      });
      if (result.outcome === 'created') {
        // ERST NACH dem Erfolg geleert (NFR-UX-005): der Entwurf ist gesichert.
        setText('');
        forgetNoteDraft(accountId, partyId);
        requestIdRef.current = undefined;
        // Mit Wiedervorlage folgt der zweite Befehl. Die Notiz wird NICHT ein
        // zweites Mal gesendet, auch wenn er scheitert (kein Sammelbefehl,
        // ADR-0052: jeder Befehl behaelt Audit und Anfragekennung).
        const vormerkung =
          gewuenschterTag === undefined
            ? undefined
            : await wiedervorlageZurNotiz(token, result.interactionId, gewuenschterTag, gesendeterAnlass);
        const stand = await load();
        // Der Anlass gehoert zu DIESER Notiz, auch wenn sie ohne Wiedervorlage
        // ging: sonst truege die naechste Notiz einen fremden Titel (Codex-Cloud-P2 #569).
        setAnlass(undefined);
        setAnlassFehler(undefined);
        if (gewuenschterTag !== undefined) {
          setFaelligkeit('keine');
          setAndererTag('');
          setAndererFehler(undefined);
        }
        if (vormerkung !== undefined && vormerkung.outcome === 'failed' && vormerkung.nichtBeschreibbar === true) {
          // Der Kontakt ist zwischen Notiz und Wiedervorlage nicht mehr
          // beschreibbar geworden: kein Formular zum Wiederholen -- es liefe
          // wieder in den Konflikt --, sondern neu lesen, damit die Leiste steht.
          setTeilerfolg(`Notiz festgehalten. Die Wiedervorlage ließ sich nicht setzen: ${vormerkung.satz}`);
          onNichtBeschreibbar?.();
          return;
        }
        if (vormerkung !== undefined && vormerkung.outcome === 'failed' && result.interactionId !== undefined) {
          setTeilerfolg(`Notiz festgehalten. Die Wiedervorlage ließ sich nicht setzen: ${vormerkung.satz}`);
          // Ein aktiver Filter koennte den neuen Eintrag ausblenden, und mit ihm
          // das Formular: dann gaebe es kein Feld fuer den Fokus.
          setFilter('alles');
          setVormerkenFuer({ interactionId: result.interactionId });
          setTermin(gewuenschterTag ?? '');
          setTitel(gesendeterAnlass);
          setTerminFehler(undefined);
          setTitelFehler(undefined);
          fokusZiel = 'vormerken';
          return;
        }
        if (vormerkung !== undefined && vormerkung.outcome === 'failed') {
          setTeilerfolg(`Notiz festgehalten. Die Wiedervorlage ließ sich nicht setzen: ${vormerkung.satz}`);
          return;
        }
        // Die Zahl kommt aus dem Ladeergebnis, nicht aus dem Zustand dieses
        // Durchlaufs -- sonst nennte die Bestaetigung den Stand von vorher.
        //
        // DER RUECKFALL OHNE ZAHL ist kein Rest des alten Textes, sondern der
        // ehrliche Satz fuer die Lage, in der es keine Zahl GIBT: `load`
        // liefert nichts, wenn der Abruf scheitert, der Lauf ueberholt wurde
        // oder die Party inzwischen weg ist. Die Notiz ist dann gespeichert,
        // der Verlauf aber nicht neu gelesen -- eine genannte Zahl waere
        // geraten. Der Normalfall ist die Variante MIT Zahl; Muster A16 misst
        // sie ("2 Notizen im Verlauf." in der Statusregion).
        setBestaetigung(
          stand === undefined
            ? 'Notiz festgehalten. Sie steht jetzt im Verlauf.'
            : `${noteSavedAnnouncement(stand.count, stand.hasMore)}${vormerkung === undefined ? '' : ' Wiedervorlage gesetzt.'}`,
        );
      } else if (result.outcome === 'invalid') {
        const erster = result.errors[0];
        setFeldfehler(erster === undefined ? 'Die Notiz wurde nicht angenommen.' : validationMessageFor(erster.fieldPath, erster.reasonCode));
        requestIdRef.current = undefined;
        fokusZiel = 'feld';
      } else if (result.outcome === 'not-found') {
        setOffen(false);
        setMessage({
          headline: 'Diesen Kontakt gibt es hier nicht mehr.',
          explanation: 'Er wurde entfernt, oder die Adresse ist nicht mehr gültig.',
          nextStep: 'Geh zurück zur Liste und suche ihn dort. Dein Text bleibt hier stehen.',
          retryable: false,
        });
      } else {
        setOffen(false);
        setMessage(result.message);
        if (result.kind === 'conflict' && result.reasonCode === 'party_not_writable') onNichtBeschreibbar?.();
        if (result.kind === 'conflict' && result.reasonCode === 'interaction_duplicate') {
          // Die Notiz IST gespeichert (erster Versuch). Text und Entwurf werden
          // geleert wie nach einem Erfolg -- sonst erzeugte der naechste Klick
          // mit neuer Kennung eine zweite, nicht loeschbare Notiz
          // (Codex-Erstreview 15.09.2026, zweiter Lauf).
          setText('');
          forgetNoteDraft(accountId, partyId);
          requestIdRef.current = undefined;
          const gesendeterText = text.trim();
          await load();
          // Die Wiederholung traf die Notiz an, nicht ihre Kennung: die
          // gewaehlte Wiedervorlage wurde nicht gesetzt, und das wird gesagt. Ist
          // die Notiz im neu geladenen Verlauf an Text und Autorin erkennbar (die
          // juengste eigene mit diesem Wortlaut und ohne eigene Wiedervorlage),
          // steht das Vormerkformular mit dem gewaehlten Tag an ihr offen -- wie
          // beim Teilerfolg. Sonst bleibt der Satz.
          if (gewuenschterTag !== undefined) {
            setTeilerfolg('Die Notiz war schon gespeichert. Setze die Wiedervorlage an der Notiz.');
            const wiedergefunden = eintraegeRef.current.find(
              // Nur Notizen: eine zugeordnete E-Mail mit gleichem Betreff ist
              // nicht die wiederholte Notiz.
              (eintrag) =>
                eintrag.interactionType === 'note' &&
                eintrag.authoredByMe &&
                eintrag.summary.trim() === gesendeterText &&
                eintrag.myOpenFollowUp === undefined,
            );
            if (wiedergefunden !== undefined) {
              setFilter('alles');
              setVormerkenFuer({ interactionId: wiedergefunden.interactionId });
              setTermin(gewuenschterTag);
              setTitel(gesendeterAnlass);
              setTerminFehler(undefined);
              setTitelFehler(undefined);
              fokusZiel = 'vormerken';
            }
          }
        } else if (result.kind === 'conflict') {
          // Nicht beschreibbar oder Kontext mehrdeutig: der Text bleibt stehen,
          // die Kennung faellt -- ein spaeterer Versuch ist eine neue Sendung.
          requestIdRef.current = undefined;
          await load();
        }
      }
    } catch {
      // Die Kennung bleibt: die Wiederholung derselben Sendung ist gewollt und legt
      // nichts doppelt an. Vor dem Senden (Anmeldung gescheitert) ist sicher nichts gespeichert.
      // Nach dem Senden still nachladen: erst dann kann der Satz auf den Verlauf
      // verweisen. Der Text im Feld bleibt dabei stehen.
      const geladen = gesendet && (await load()) !== undefined;
      setOffen(gesendet);
      setMessage(
        gesendet
          ? {
              headline: 'Ob die Notiz gespeichert wurde, ist offen.',
              explanation: 'Die Antwort kam nicht an. Dein Text bleibt stehen.',
              nextStep: geladen
                ? 'Versuche es erneut; mit unverändertem Text wird nichts doppelt gespeichert. Hast du den Text geändert, sieh vorher im Verlauf nach, ob die Notiz schon dasteht.'
                : 'Versuche es erneut; mit unverändertem Text wird nichts doppelt gespeichert.',
              retryable: true,
            }
          : {
              headline: 'Die Notiz wurde nicht gespeichert.',
              explanation: 'Deine Anmeldung ließ sich gerade nicht erneuern. Gesendet wurde nichts.',
              nextStep: 'Dein Text bleibt stehen. Versuche es erneut.',
              retryable: true,
            },
      );
    } finally {
      setBusy(false);
      if (fokusZiel === 'feld') {
        document.getElementById(NOTE_INPUT_ID)?.focus();
      } else if (fokusZiel === 'vormerken') {
        setFokusVormerken((zahl) => zahl + 1);
      } else {
        setFokusAnfordern((zahl) => zahl + 1);
      }
    }
  };

  /**
   * Der zweite Befehl der Notiz mit Wiedervorlage, mit dem Anlass aus dem
   * Notizkasten als Titel (leer: die Vorbelegung des Servers). Fehlt die
   * Kennung der neuen Notiz, gilt das wie ein Fehlschlag des Befehls.
   */
  const wiedervorlageZurNotiz = async (
    token: string,
    interactionId: string | undefined,
    dueOn: string,
    titel: string,
  ): Promise<{ readonly outcome: 'gesetzt' } | { readonly outcome: 'failed'; readonly satz: string; readonly nichtBeschreibbar?: true }> => {
    if (interactionId === undefined) {
      return { outcome: 'failed', satz: 'Setze sie an der Notiz noch einmal.' };
    }
    try {
      const ergebnis = await createFollowUp({ baseUrl: apiBaseUrl, accessToken: token, partyId, interactionId, dueOn, title: titel });
      if (ergebnis.outcome === 'applied') {
        return { outcome: 'gesetzt' };
      }
      if (ergebnis.outcome === 'field-rejected') {
        return { outcome: 'failed', satz: ergebnis.message };
      }
      if (ergebnis.outcome === 'gone') {
        return { outcome: 'failed', satz: 'Die Notiz gibt es nicht mehr. Lade den Verlauf neu.' };
      }
      const satz = `${ergebnis.message.headline} ${ergebnis.message.nextStep}`;
      return ergebnis.reasonCode === 'party_not_writable' ? { outcome: 'failed', satz, nichtBeschreibbar: true } : { outcome: 'failed', satz };
    } catch {
      return { outcome: 'failed', satz: 'LIAISON ist gerade nicht erreichbar. Setze sie an der Notiz noch einmal.' };
    }
  };

  /**
   * Der gemeinsame Ausgang aller vier Befehle der Wiedervorlage. Er schreibt in
   * DIESELBE Statusregion wie die Notiz und laedt den Verlauf nach, damit die
   * Zeile den Stand zeigt, den der Server kennt -- und nicht den, den der
   * Browser sich gemerkt hat.
   */
  const nachBefehl = async (
    ergebnis: Awaited<ReturnType<typeof completeFollowUp>>,
    bestaetigt: string,
  ): Promise<'ok' | 'feld' | 'fehler'> => {
    if (ergebnis.outcome === 'applied') {
      setBestaetigung(bestaetigt);
      setTeilerfolg(undefined);
      setOffen(false);
      setMessage(undefined);
      setVormerkenFuer(undefined);
      setTermin('');
      setTitel('');
      await load();
      return 'ok';
    }
    if (ergebnis.outcome === 'field-rejected') {
      // Ein abgelehntes FELD schickt den Fokus zurueck ins Feld, nicht in die
      // Statusregion -- dieselbe Regel wie bei der Notiz (WCAG 2.4.3).
      setTerminFehler(ergebnis.message);
      return 'feld';
    }
    setBestaetigung(undefined);
    if (ergebnis.outcome === 'gone') {
      setOffen(false);
      setMessage({
        headline: 'Diese Wiedervorlage gibt es nicht mehr.',
        explanation: 'Sie wurde entfernt, oder du darfst sie nicht mehr sehen.',
        nextStep: 'Lade den Verlauf neu, um den aktuellen Stand zu sehen.',
        retryable: true,
      });
    } else {
      setOffen(false);
      setMessage(ergebnis.message);
      // Der Kontakt ist inzwischen nicht mehr beschreibbar: die Seite liest ihn
      // neu, damit die Leiste den Zustand zeigt -- wie bei der Notiz.
      if (ergebnis.reasonCode === 'party_not_writable') onNichtBeschreibbar?.();
    }
    // Ein abgelehnter Befehl laesst den Stand veraltet zuruck -- neu laden,
    // damit der naechste Klick nicht wieder auf denselben Konflikt laeuft.
    await load();
    return 'fehler';
  };

  /**
   * Der Rahmen jedes Befehls: Token holen, laufen lassen, Fokus setzen. Wohin
   * der Fokus geht, sagt der Lauf SELBST zurueck -- ein abgewiesenes Feld
   * schickt ihn dorthin zurueck, alles andere in die Statusregion.
   */
  const mitToken = async (verb: string, lauf: (token: string) => Promise<'ok' | 'feld' | 'fehler'>): Promise<void> => {
    setBusy(true);
    let ziel: 'status' | 'feld' = 'status';
    let gesendet = false;
    try {
      const token = await accessToken();
      if (token === undefined) {
        onReauthenticate();
        return;
      }
      gesendet = true;
      if ((await lauf(token)) === 'feld') ziel = 'feld';
    } catch {
      setBestaetigung(undefined);
      setOffen(gesendet);
      setMessage(gesendet ? wiedervorlageOffen(verb) : WIEDERVORLAGE_NICHT_GESENDET);
    } finally {
      setBusy(false);
      if (ziel === 'feld') {
        document.getElementById(FOLLOW_UP_DATE_ID)?.focus();
      } else {
        setFokusAnfordern((zahl) => zahl + 1);
      }
    }
  };

  const vormerkenSenden = async (event: FormEvent<HTMLFormElement>, eintrag: InteractionView): Promise<void> => {
    event.preventDefault();
    const offen = vormerkenFuer;
    const terminProblem = dueOnFieldProblem(termin, heute);
    const titelProblem = titleFieldProblem(titel);
    setTerminFehler(terminProblem);
    setTitelFehler(titelProblem);
    if (terminProblem !== undefined || titelProblem !== undefined) {
      // In das Feld, das WIRKLICH abgewiesen wurde (Codex-Cloud-Review PR #251,
      // P2). Ist nur der Titel zu lang, waere der Sprung ins gueltige
      // Datumsfeld eine Irrefuehrung -- und der Fehler steht womoeglich
      // ausserhalb des sichtbaren Bereichs (WCAG 2.4.3, 3.3.1).
      document.getElementById(terminProblem !== undefined ? FOLLOW_UP_DATE_ID : FOLLOW_UP_TITLE_ID)?.focus();
      return;
    }
    setOffen(false);
    setMessage(undefined);
    setBestaetigung(undefined);
    await mitToken('geändert', async (token) => {
      // Verschieben, wenn es schon eine gibt -- sonst anlegen. Der Unterschied
      // steht im Zustand und nicht in einem zweiten Formular.
      const ergebnis =
        offen?.taskId === undefined || offen.lockVersion === undefined
          ? await createFollowUp({
              baseUrl: apiBaseUrl,
              accessToken: token,
              partyId,
              interactionId: eintrag.interactionId,
              dueOn: termin,
              title: titel,
            })
          : await changeFollowUpDue({
              baseUrl: apiBaseUrl,
              accessToken: token,
              taskId: offen.taskId,
              lockVersion: offen.lockVersion,
              dueOn: termin,
            });
      const ausgang = await nachBefehl(
        ergebnis,
        offen?.taskId === undefined ? 'Wiedervorlage gesetzt. Sie steht in deiner Liste.' : 'Termin geändert.',
      );
      if (ausgang === 'ok') {
        setErledigt((alt) => {
          const neu = new Set(alt);
          neu.delete(eintrag.interactionId);
          return neu;
        });
      }
      return ausgang;
    });
  };

  const abhaken = async (eintrag: InteractionView, art: 'erledigt' | 'zurueckgezogen'): Promise<void> => {
    const vormerkung = eintrag.myOpenFollowUp;
    if (vormerkung === undefined) return;
    setOffen(false);
    setMessage(undefined);
    setBestaetigung(undefined);
    await mitToken(art === 'erledigt' ? 'erledigt' : 'zurückgezogen', async (token) => {
      const optionen = { baseUrl: apiBaseUrl, accessToken: token, taskId: vormerkung.taskId, lockVersion: vormerkung.lockVersion };
      const ergebnis = art === 'erledigt' ? await completeFollowUp(optionen) : await cancelFollowUp(optionen);
      const satz = art === 'erledigt' ? ERLEDIGT_SATZ : nurLesen ? 'Zurückgezogen.' : 'Zurückgezogen. Brauchst du sie doch, setz an der Notiz eine neue.';
      const ausgang = await nachBefehl(ergebnis, satz);
      if (ausgang === 'ok' && art === 'erledigt' && ergebnis.outcome === 'applied' && !nurLesen) {
        setRueckgang({ satz, taskId: ergebnis.taskId, lockVersion: ergebnis.lockVersion, interactionId: eintrag.interactionId });
      }
      if (ausgang === 'ok') {
        // "erledigt" gilt nur, solange nichts Neues daran haengt: zurueckgezogen
        // oder neu gesetzt loescht die Marke.
        setErledigt((alt) => {
          const neu = new Set(alt);
          if (art === 'erledigt') neu.add(eintrag.interactionId);
          else neu.delete(eintrag.interactionId);
          return neu;
        });
      }
      return ausgang;
    });
  };

  const wiederOeffnen = async (ziel: NonNullable<typeof rueckgang>): Promise<void> => {
    await mitToken('wieder geöffnet', async (token) => {
      let ergebnis: Awaited<ReturnType<typeof reopenFollowUp>>;
      try {
        ergebnis = await reopenFollowUp({ baseUrl: apiBaseUrl, accessToken: token, taskId: ziel.taskId, lockVersion: ziel.lockVersion });
      } catch {
        // Kein Netz: die Ansage "Erledigt." und ihr Knopf bleiben, damit ein zweiter Versuch moeglich ist.
        setOffen(true);
        setMessage(wiedervorlageOffen('wieder geöffnet'));
        return 'fehler';
      }
      // Der Knopf verschwindet in jedem Fall: nach Erfolg und nach Ablehnung ist der Stand ein anderer.
      setRueckgang(undefined);
      // Die Antwort traegt kein `overdue`; der Kalendertag bleibt, also entscheidet das Heute.
      const satz = ergebnis.outcome === 'applied' ? `Wieder offen, ${dueLabel(ergebnis.dueOn, ergebnis.dueOn < todayInBerlin())}.` : '';
      const ausgang = await nachBefehl(ergebnis, satz);
      if (ausgang === 'ok') {
        setErledigt((alt) => {
          const neu = new Set(alt);
          neu.delete(ziel.interactionId);
          return neu;
        });
      }
      return ausgang;
    });
  };

  const optionen = filterOptionen(eintraege);
  // Im Ueberblick die neuesten Eintraege ohne Filter: ein im Register gewaehlter
  // Filter gilt dort, nicht hier (M-12).
  const sichtbar = kompakt ? eintraege.slice(0, KOMPAKT_EINTRAEGE) : filtern(eintraege, filter);
  const ganzerVerlaufZahl = `${eintraege.length}${hasMore ? '+' : ''}`;
  // Gleich betitelte eigene Wiedervorlagen: ihre Knoepfe hiessen sonst gleich
  // (WCAG 2.4.6); dann haengt der Name den Notizbezug an.
  const titelZahl = new Map<string, number>();
  for (const eintrag of sichtbar) {
    const titel = eintrag.myOpenFollowUp?.title.trim();
    if (titel !== undefined) titelZahl.set(titel, (titelZahl.get(titel) ?? 0) + 1);
  }
  // Heissen sie auch mit dem Notizbezug noch gleich (zwei Notizen derselben Art in
  // derselben Minute), nennt der Name die Stelle unter ihnen (Codex-P2 #575).
  const titelDoppelt = (eintrag: InteractionView): boolean =>
    eintrag.myOpenFollowUp !== undefined && (titelZahl.get(eintrag.myOpenFollowUp.title.trim()) ?? 0) > 1;
  const knopfSchluessel = (eintrag: InteractionView): string | undefined => {
    const offen = eintrag.myOpenFollowUp;
    if (offen === undefined) return undefined;
    const bezug = notizBezugVon(eintrag);
    const name = followUpButtonName('', offen.title, bezug);
    return titelDoppelt(eintrag) && !name.includes(bezug) ? `${name}, ${bezug}` : name;
  };
  const gleicheNamen = new Map<string, string[]>();
  for (const eintrag of sichtbar) {
    const schluessel = knopfSchluessel(eintrag);
    if (schluessel !== undefined) gleicheNamen.set(schluessel, [...(gleicheNamen.get(schluessel) ?? []), eintrag.interactionId]);
  }
  const stelleUnterGleichen = (eintrag: InteractionView): string | undefined => {
    const schluessel = knopfSchluessel(eintrag);
    const gruppe = schluessel === undefined ? undefined : gleicheNamen.get(schluessel);
    if (gruppe === undefined || gruppe.length < 2) return undefined;
    return `${gruppe.indexOf(eintrag.interactionId) + 1}. von ${gruppe.length}`;
  };

  // Dieselbe Art in derselben Minute: "Ganze Notiz, …" nennt dann die Stelle unter ihnen (WCAG 2.4.6).
  const gleicherBezug = new Map<string, string[]>();
  for (const eintrag of sichtbar) {
    const bezug = notizBezugVon(eintrag);
    gleicherBezug.set(bezug, [...(gleicherBezug.get(bezug) ?? []), eintrag.interactionId]);
  }
  const stelleGleicherBezug = (eintrag: InteractionView): string | undefined => {
    const gruppe = gleicherBezug.get(notizBezugVon(eintrag)) ?? [];
    return gruppe.length < 2 ? undefined : `${gruppe.indexOf(eintrag.interactionId) + 1}. von ${gruppe.length}`;
  };

  /** Abbrechen und Esc: Formular zu, Fokus zurueck an den Ausloeser (nie body). */
  const vormerkenAbbrechen = (interactionId: string): void => {
    setFokusAusloeser(interactionId);
    setVormerkenFuer(undefined);
    setTerminFehler(undefined);
    setTitelFehler(undefined);
  };

  const vormerkformular = (eintrag: InteractionView): ReactElement | undefined =>
    vormerkenFuer?.interactionId !== eintrag.interactionId ? undefined : (
      <form
        className={own.vormerkformular}
        noValidate
        onSubmit={(event) => void vormerkenSenden(event, eintrag)}
        // Esc wirkt wie "Abbrechen" (DESIGN-P3 #540), ausser es schliesst eine offene Auswahlliste.
        onKeyDown={(event) => {
          if (!busy) beiEscapeSchliessen(event, () => vormerkenAbbrechen(eintrag.interactionId));
        }}
      >
        <Field
          label="Für welchen Tag vormerken?"
          validationMessage={terminFehler ?? null}
          validationState={terminFehler === undefined ? 'none' : 'error'}
        >
          <Input
            id={FOLLOW_UP_DATE_ID}
            type="date"
            value={termin}
            min={heute}
            disabled={busy}
            onChange={(_, data) => {
              setTermin(data.value);
              if (terminFehler !== undefined) setTerminFehler(undefined);
            }}
          />
        </Field>
        {vormerkenFuer.taskId === undefined ? (
          <Field
            label="Worum geht es? (kannst du weglassen)"
            validationMessage={titelFehler ?? null}
            validationState={titelFehler === undefined ? 'none' : 'error'}
          >
            <Input
              id={FOLLOW_UP_TITLE_ID}
              value={titel}
              disabled={busy}
              placeholder="Wiedervorlage"
              onChange={(_, data) => {
                setTitel(data.value);
                if (titelFehler !== undefined) setTitelFehler(undefined);
              }}
            />
          </Field>
        ) : undefined}
        <Button appearance="secondary" type="submit" disabled={busy}>
          {vormerkenFuer.taskId === undefined ? 'Wiedervorlage setzen' : 'Termin ändern'}
        </Button>
        <NebenKnopf
          type="button"
          disabled={busy}
          onClick={() => vormerkenAbbrechen(vormerkenFuer.interactionId)}
        >
          Abbrechen
        </NebenKnopf>
      </form>
    );

  return (
    <section className={own.gruppe} aria-labelledby="verlauf">
      <Subtitle1 as="h2" id="verlauf" block>
        Verlauf
      </Subtitle1>

      {nurLesen ? undefined : (
      <form
        className={mergeClasses(own.notiz, own.eingabeBehaelter)}
        noValidate
        onSubmit={(event) => void festhalten(event)}
        onKeyDown={(event) => {
          // Strg/Cmd+Enter sendet ab, auch aus der Auswahl der Wiedervorlage.
          if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
            event.preventDefault();
            event.currentTarget.requestSubmit();
          }
        }}
      >
        {/* Der Sichtbarkeitssatz ist eine Datenschutzaussage und steht am Feld,
            sichtbar. */}
        <Field
          // Die Beschriftung zeigt auf das Feld mit der festen Kennung; Fluent
          // wuerde sonst seine eigene erzeugen.
          label={{ children: 'Notiz', htmlFor: NOTE_INPUT_ID, className: own.notizBeschriftung }}
          hint="Deine Notiz sehen alle in deinem Verlag, die diesen Kontakt sehen dürfen."
          validationMessage={feldfehler ?? null}
          validationState={feldfehler === undefined ? 'none' : 'error'}
        >
          {() => (
            <div className={mergeClasses(own.eingabeKasten, feldfehler !== undefined && own.eingabeKastenFehler)} data-eingabekasten="">
              <Textarea
                id={NOTE_INPUT_ID}
                value={text}
                resize="vertical"
                rows={2}
                disabled={busy}
                aria-keyshortcuts="Control+Enter Meta+Enter"
                onChange={(_, data) => {
                  setText(data.value);
                  if (feldfehler !== undefined) setFeldfehler(undefined);
                }}
              />
              <div className={own.eingabeFuss}>
                <div className={mergeClasses(own.eingabeLinks, faelligkeit !== 'keine' && own.eingabeLinksVoll)}>
                  <Field
                    label="Wiedervorlage"
                    orientation="horizontal"
                    className={own.wiedervorlageFeld}
                  >
                    <Dropdown
                      id={FOLLOW_UP_CHOICE_ID}
                      className={own.wiedervorlageWahl}
                      value={
                        FAELLIGKEITEN.find(
                          (option) => option.id === faelligkeit,
                        )?.label ?? ""
                      }
                      selectedOptions={[faelligkeit]}
                      disabled={busy}
                      onOptionSelect={(_, data) => {
                        setFaelligkeit(
                          (data.optionValue ?? "keine") as FaelligkeitId,
                        );
                        setAndererFehler(undefined);
                      }}
                    >
                      {FAELLIGKEITEN.map((option) => (
                        <Option key={option.id} value={option.id}>
                          {option.label}
                        </Option>
                      ))}
                    </Dropdown>
                  </Field>
                  {faelligkeit === "anderer" ? (
                    <Field
                      label="Anderer Tag"
                      validationMessage={andererFehler ?? null}
                      validationState={
                        andererFehler === undefined ? "none" : "error"
                      }
                    >
                      <Input
                        id={FOLLOW_UP_OTHER_DAY_ID}
                        type="date"
                        value={andererTag}
                        min={heute}
                        disabled={busy}
                        onChange={(_, data) => {
                          setAndererTag(data.value);
                          if (andererFehler !== undefined)
                            setAndererFehler(undefined);
                        }}
                      />
                    </Field>
                  ) : undefined}
                  {/* Mit Wiedervorlage: worum es geht, vorbelegt mit dem Notizanfang
                      (D-15). Eine Zeile, sichtbar und aenderbar; der Titel ist so
                      sichtbar wie die Notiz (ADR-0052 Ziffer 3). */}
                  {faelligkeit !== 'keine' ? (
                    <Field
                      label="Worum geht es?"
                      orientation="horizontal"
                      className={own.anlassFeld}
                      validationMessage={anlassFehler ?? null}
                      validationState={anlassFehler === undefined ? 'none' : 'error'}
                    >
                      <Input
                        id={FOLLOW_UP_REASON_ID}
                        value={anlassWert}
                        placeholder="Wiedervorlage"
                        disabled={busy}
                        onChange={(_, data) => {
                          setAnlass(data.value);
                          if (anlassFehler !== undefined) setAnlassFehler(undefined);
                        }}
                      />
                    </Field>
                  ) : undefined}
                </div>
                <div className={own.eingabeRechts}>
                  <Caption1 className={own.kuerzelHinweis}>
                    {/* Dieselben Kappen wie Kopf und Palette (shell/taste.tsx, DESIGN-P2 #496). */}
                    <Tasten folge={mac ? '⌘ Enter' : 'Strg Enter'} />
                  </Caption1>
                  {/* Sichtbar kurz wie v4; der Name sagt, was festgehalten wird, und
                      enthaelt das sichtbare Wort (WCAG 2.5.3). */}
                  <Button appearance="primary" type="submit" disabled={busy} aria-label="Notiz festhalten">
                    Festhalten
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Field>
      </form>
      )}

      {/* `aria-label`, damit die Herkunft der Meldung nicht allein am
          vorgelesenen Satz haengt (Experience-Review 17.09.2026, F2): kurze
          Saetze wie "Termin geändert." tragen ihren Abschnitt sonst nicht,
          und mit dem dritten Abschnitt ("Stichworte") waechst der Bedarf. */}
      <div aria-live="polite" role="status" aria-label="Status: Verlauf" id={HISTORY_STATUS_ID} ref={statusRef} tabIndex={-1} className={own.hinweis}>
        {status === 'laedt' ? <Skelett ladesatz="Verlauf wird geladen" zeilen={SKELETT_VERLAUF} /> : undefined}
        {bestaetigung !== undefined ? (
          <MessageBar intent="success">
            <MessageBarBody>
              <MessageBarTitle>Gespeichert</MessageBarTitle>
              <span>{bestaetigung}</span>
            </MessageBarBody>
            {rueckgang !== undefined && rueckgang.satz === bestaetigung ? (
              <MessageBarActions>
                <Button appearance="secondary" size="small" disabled={busy} onClick={() => void wiederOeffnen(rueckgang)}>
                  Rückgängig
                </Button>
              </MessageBarActions>
            ) : undefined}
          </MessageBar>
        ) : undefined}
        {teilerfolg !== undefined ? (
          <MessageBar intent="warning">
            <MessageBarBody>
              <MessageBarTitle>Teilweise gespeichert</MessageBarTitle>
              <div>{teilerfolg}</div>
            </MessageBarBody>
          </MessageBar>
        ) : undefined}
        {message !== undefined ? (
          <MessageBar intent={meldungsIntent(message, offen)}>
            <MessageBarBody>
              <MessageBarTitle>{message.headline}</MessageBarTitle>
              <div>{message.explanation}</div>
              <div>{message.nextStep}</div>
            </MessageBarBody>
          </MessageBar>
        ) : undefined}
        {ladefehler !== undefined ? (
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>{ladefehler.message.headline}</MessageBarTitle>
              <div>{ladefehler.message.explanation}</div>
              <div>{ladefehler.message.nextStep}</div>
            </MessageBarBody>
          </MessageBar>
        ) : undefined}
      </div>
      {/* Die Vorgangsnummer steht UNTER der Statusregion, nicht darin: ihr
          Kopierknopf hat eine eigene Ansage, und verschachtelte Live-Regionen
          lesen Screenreader doppelt oder gar nicht (Codex-Cloud-P1 #540). */}
      {message?.correlationId !== undefined ? <Vorgang nummer={message.correlationId} eingebettet /> : undefined}
      {ladefehler?.message.correlationId !== undefined ? <Vorgang nummer={ladefehler.message.correlationId} eingebettet /> : undefined}

      {/* Ohne Notiz steht darueber die Eingabe, an einem nicht beschreibbaren Kontakt die Leiste
          des Kopfs; der Block traegt nur den Satz. */}
      {status === 'geladen' && eintraege.length === 0 ? <Zustand art="leer" titel={historySummary(0, false)} saetze={[]} eingebettet /> : undefined}

      {status === 'geladen' && optionen.length > 0 && !kompakt ? (
        <Schaltergruppe
          label="Verlauf filtern"
          optionen={optionen}
          gewaehlt={filter}
          onWahl={(id) => setFilter(id as VerlaufFilterId)}
          className={own.filterLeiste}
          gruppeRef={filterLeisteRef}
        />
      ) : undefined}

      {/* Bei "Alles" sagt die Liste die Zahl selbst und "Aeltere Notizen laden"
          sagt, dass es mehr gibt; die Zeile stuende sonst doppelt unter der
          Bestaetigung der Statusregion. Nur bei aktivem Filter erklaert sie,
          was er verbirgt. Sie steht UNTER den Pillen, damit die angeklickte
          Pille beim Umschalten nicht unter dem Zeiger wegspringt. Der
          Filterwechsel sagt sie nicht an: aria-pressed reicht. */}
      {status === 'geladen' && eintraege.length > 0 && filter !== 'alles' && !kompakt ? (
        <Caption1 className={own.hinweis}>{filteredHistorySummary(sichtbar.length, eintraege.length, hasMore)}</Caption1>
      ) : undefined}

      {status === 'geladen' && eintraege.length > 0 && sichtbar.length === 0 && !kompakt ? (
        <Zustand art="leer" titel={emptyFilterSentence(filter)} saetze={[]} eingebettet haupt={{ label: 'Alles zeigen', onClick: () => { setFilter('alles'); setFilterFokus(true); } }} />
      ) : undefined}

      {status === 'geladen' && sichtbar.length > 0 ? (
        <ul className={own.zeitleiste} aria-label="Notizen">
          {sichtbar.map((eintrag, stelle) => (
            <VerlaufEintrag
              key={eintrag.interactionId}
              eintrag={eintrag}
              titelDoppelt={titelDoppelt(eintrag)}
              stelleUnterGleichen={stelleUnterGleichen(eintrag)}
              stelleGleicherBezug={stelleGleicherBezug(eintrag)}
              kurz={kompakt}
              letzter={stelle === sichtbar.length - 1}
              busy={busy}
              erledigt={erledigt.has(eintrag.interactionId)}
              formular={vormerkformular(eintrag)}
              onSetzen={() => {
                setVormerkenFuer({ interactionId: eintrag.interactionId });
                setTermin('');
                // Der Notizanfang als Anlass, sichtbar und aenderbar (D-15).
                setTitel(anlassAusNotiz(eintrag.summary));
                setTerminFehler(undefined);
                setTitelFehler(undefined);
                // Der Fokus geht ins Datumsfeld, sobald es eingehaengt ist -- nie auf body (WCAG 2.4.3).
                setFokusVormerken((zahl) => zahl + 1);
              }}
              onAbhaken={(art) => void abhaken(eintrag, art)}
              nurLesen={nurLesen}
              onVerschieben={() => {
                const offen = eintrag.myOpenFollowUp;
                if (offen === undefined) return;
                setVormerkenFuer({ interactionId: eintrag.interactionId, taskId: offen.taskId, lockVersion: offen.lockVersion });
                setTermin(offen.dueOn);
                setTerminFehler(undefined);
                // Der Ausloeser im Menue "…" verschwindet mit dem Formular: der Fokus geht ins Datumsfeld.
                setFokusVormerken((zahl) => zahl + 1);
              }}
            />
          ))}
        </ul>
      ) : undefined}

      <div className={styles.actions}>
        {/* Im Ueberblick fuehrt der Weg ins Register, die Zahl ist die am Register
            ("Verlauf (12)"); "+" nur sichtbar, vorgelesen "N oder mehr" (Glossar). */}
        {status === 'geladen' && kompakt && onGanzerVerlauf !== undefined && (eintraege.length > KOMPAKT_EINTRAEGE || hasMore) ? (
          <Button
            appearance="subtle"
            className={own.ganzerVerlauf}
            icon={<VerlaufSymbol />}
            iconPosition="before"
            aria-label={`Ganzen Verlauf anzeigen, ${eintraege.length}${hasMore ? ' oder mehr' : ''} Einträge`}
            onClick={() => {
              setFokusGanzer((zahl) => zahl + 1);
              onGanzerVerlauf();
            }}
          >
            Ganzen Verlauf anzeigen ({ganzerVerlaufZahl})
          </Button>
        ) : undefined}
        {status === 'geladen' && hasMore && !kompakt ? (
          <Button appearance="secondary" disabled={busy} onClick={() => void load(cursor)}>
            Ältere Notizen laden
          </Button>
        ) : undefined}
        {ladefehler !== undefined && ladefehler.message.retryable ? (
          // Die GESCHEITERTE Seite wird wiederholt -- mit ihrem Cursor, nicht
          // die erste Seite (Muster A12 des Lesewegs).
          <Button appearance="secondary" disabled={busy} onClick={() => void load(ladefehler.cursor)}>
            {ladefehler.cursor === undefined ? 'Verlauf erneut laden' : 'Ältere Notizen erneut laden'}
          </Button>
        ) : undefined}
      </div>
    </section>
  );
}

// ============================================================================
// DER ABSCHNITT "STICHWORTE" (ST-TAX-002, erster Schnitt; ADR-0053)
// ============================================================================
//
// WAS ER NICHT ZEIGT (ADR-0002: keine Attrappe): keine Suche in der Auswahl --
// das Startvokabular hat zwoelf Werte, und die Suche kommt mit dem
// Reviewpunkt aus ADR-0053 Ziffer 9; keine Massenpflege, keine Vorschlaege,
// keine persoenlichen Merkzeichen -- sie sind spaetere Schnitte, und ein
// abgeblendeter Knopf fuer etwas, das es nicht gibt, ist eine Attrappe; keine
// Achse, keine Version und keinen Lebenszyklus in der Auswahl -- dort stehen
// Begriff und Definition (5.2 Nr. 9).
//
// KONTEXT UND ROLLE SIND KEINE EINGABE: beides entscheidet der Server und
// erklaert es in Worten (5.2 Nr. 14).
